import React, { useEffect, useState } from "react";
import { FormControl, InputLabel, MenuItem, Select } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import FirmwareTable from "./FirmwareTable";
import usePermissionCheck from "../../../hooks/usePermissionCheck";
import { FilterLineIcon, PlusIcon } from "../../../components/common/Icons/Icons";
import { palName } from "../../../utils/brand";

// URL params the filter panel owns (counted on the Filters button)
const filterParams = ['type', 'visibility'];

interface ContainerProps {
  setEditOpen: any;
  setDeleteOpen: any;
  setViewOpen: any;
  setUploadOpen: any;
  setFirmwareToModify: any;
}

const FirmwaresContainer: React.FC<ContainerProps> = ({setEditOpen, setDeleteOpen, setViewOpen, setUploadOpen, setFirmwareToModify}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { canAccess: canViewAdmin } = usePermissionCheck(['view_admin_firmware']);

  // Open by default: every filter in view
  const [showFilters, setShowFilters] = useState(true);

  const [type, setType] = useState('');
  const [visibility, setVisibility] = useState('');

  useEffect(() => {
    setType(searchParams.get('type') ?? '');
    setVisibility(searchParams.get('visibility') ?? '');
  }, [searchParams]);

  const handleFilter = () => {
    searchParams.delete('page'); //reset page
    type === "" ? searchParams.delete('type') : searchParams.set('type', type);
    visibility === "" ? searchParams.delete('visibility') : searchParams.set('visibility', visibility);
    setSearchParams(searchParams);
  };

  const handleClear = () => {
    setType("");
    setVisibility("");
    searchParams.delete('type');
    searchParams.delete('visibility');
    searchParams.delete('page'); //reset page
    setSearchParams(searchParams);
  };

  const activeFilters = filterParams.filter((param) => searchParams.has(param)).length;

  // Who can see the firmware: the options name the filter, so no label
  const visibilityOptions: [string, string][] = [['', 'All'], ['public', 'Public'], ['dev', 'Developer']];
  if (canViewAdmin) visibilityOptions.push(['admin', 'Admin']);

  const toolbar = (
    <>
      <button
        type="button"
        className={`btn btn-sm dt-tool-btn lp-filter-toggle ${showFilters ? 'active' : ''}`}
        onClick={() => setShowFilters(!showFilters)}
        aria-expanded={showFilters}
        aria-controls="lp-filters"
      >
        <FilterLineIcon/> Filters
        {activeFilters > 0 && <span className="lp-count">{activeFilters}</span>}
      </button>

      <button type="button" className="btn btn-sm btn-primary lp-primary-action" onClick={() => setUploadOpen(true)}>
        <PlusIcon/> Upload Firmware
      </button>
    </>
  );

  return (
    <div className={`lp-layout ${showFilters ? '' : 'filters-hidden'}`}>
      {showFilters && (
        <aside className="lp-filters" id="lp-filters" aria-label="Filters">
          <div className="lp-filters-body">
            <div className="lp-group">
              <FormControl fullWidth size="small">
                <InputLabel id="firmware-type-label">Firmware</InputLabel>
                <Select
                  labelId="firmware-type-label"
                  label="Firmware"
                  value={type ?? ''}
                  onChange={(e) => setType(e.target.value)}
                >
                  <MenuItem value={''}>All</MenuItem>
                  <MenuItem value={'yocto'}>Yocto</MenuItem>
                  <MenuItem value={'lpc'}>LPC</MenuItem>
                  <MenuItem value={'spaboy'}>{palName}</MenuItem>
                </Select>
              </FormControl>
            </div>

            <div className="lp-group">
              <div className="segmented lp-segmented" role="group" aria-label="Visibility">
                {visibilityOptions.map(([option, text]) => (
                  <button key={option} type="button" className={visibility === option ? 'active' : ''} aria-pressed={visibility === option} onClick={() => setVisibility(option)}>
                    {text}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="lp-filters-foot">
            <button type="button" className="btn btn-sm lp-clear-btn" onClick={handleClear}>Clear</button>
            <button type="button" className="btn btn-sm btn-primary" onClick={handleFilter}>Apply Filters</button>
          </div>
        </aside>
      )}

      <FirmwareTable
        setEditOpen={setEditOpen}
        setDeleteOpen={setDeleteOpen}
        setViewOpen={setViewOpen}
        setFirmwareToModify={setFirmwareToModify}
        toolbar={toolbar}
      />
    </div>
  );
};

export default FirmwaresContainer;
